import { forwardRef } from 'react'
import { Field } from 'formik'
import MaskedInput from 'react-text-mask'
import Icon from 'components/Icon'
import If from '@mobi/components/If'
import { useValidate } from '../hooks'
import { useState, useEffect } from 'react'
import classNames from 'classnames'

const InputTextAnimation = forwardRef(
  (
    {
      name = '',
      placeholder = '',
      label = '',
      mask = () => false,
      rules,
      errors = {},
      type = 'text',
      touched,
      handleChange,
      handleBlur,
      icon,
      inputMode,
      dataTest,
      errorColor,
      initialValues,
      isAutoFillFields,
      ...props
    },
    ref
  ) => {
    const fieldId = `field-${name}`

    const validate = useValidate(rules)

    const [inputFocused, setInputFocused] = useState('')
    const [inputFilled, setInputFilled] = useState(false)

    const handleOnChangeInputAnimated = (evt) => {
      handleChange(evt)
      setInputFocused(evt.target.value)
    }

    const handleBlurInput = (evt) => {
      handleBlur(evt)
      setInputFocused(!inputFocused)
      if (inputFocused && evt.currentTarget.value) {
        setInputFilled(true)
      }
    }

    const handleClickInput = (evt) => {
      setInputFocused(true)
      if (touched && !evt.currentTarget.value) {
        setInputFilled(false)
      }
    }

    const handleTabPress = (evt) => {
      if (evt.key === 'Tab') {
        setInputFocused(true)
      }
    }

    useEffect(() => {
      for (const property in initialValues) {
        if (property === name && initialValues[property]) {
          setInputFilled(true)
        }
      }
    }, [])

    useEffect(() => {
      isAutoFillFields && setInputFilled(true)
    }, [isAutoFillFields])

    return (
      <div
        className={classNames('input-container', {
          focused: !!inputFocused,
          filled: !!inputFilled,
          error: errors[name] && touched[name],
        })}
      >
        <label className="label" htmlFor={fieldId}>
          {label}
        </label>
        <Field name={name} validate={validate}>
          {({ field }) => {
            const inputProps = {
              ...field,
              className: 'input',
              id: fieldId,
              placeholder: placeholder,
              type: type,
              onChange: handleOnChangeInputAnimated,
              onBlur: handleBlurInput,
              onClick: handleClickInput,
              inputMode: inputMode,
              'data-test': dataTest,
              ...props,
            }

            return (
              <div className="input-content">
                {icon && (
                  <div className="icon-content">
                    <Icon iconId={icon} />
                  </div>
                )}
                <MaskedInput
                  {...inputProps}
                  mask={mask}
                  guide={false}
                  onKeyUp={handleTabPress}
                  ref={ref}
                />
              </div>
            )
          }}
        </Field>
        <If
          condition={errors[name] && touched[name]}
          renderIf={
            <div
              className="error-default"
              data-test="input-error-message"
              aria-live="polite"
              aria-label={`${errors[name]} - ${label}`}
              role="alert"
            >
              <span
                className="text"
                aria-hidden={true}
                style={errorColor ? { color: errorColor } : null}
              >
                {errors[name]}
              </span>
            </div>
          }
        />
      </div>
    )
  }
)

InputTextAnimation.displayName = 'InputTextAnimation'

export default InputTextAnimation
